import React, { useState, useEffect } from 'react';
import { Card, Typography, Empty, Spin, Button, Tag, Space, Divider } from 'antd';
import { 
  CalendarOutlined, 
  ClockCircleOutlined, 
  EnvironmentOutlined,
  ReloadOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { subscribeToSchedules } from '../../firebase/scheduleService';
import staffAuthManager from '../../firebase/staffAuthService';
import { 
  getSingaporeDate, 
  getSingaporeDateRange, 
  getStandardizedDisplayDate,
  formatDateShort,
  getDayName,
  isToday,
  isTomorrow
} from '../../utils/dateUtils';

const { Title, Text } = Typography;

const StaffScheduleView = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Calculate 7-day date range (today + next 6 days) using Singapore timezone
  const getDateRange = () => {
    // Use standardized Singapore timezone calculation
    const today = getSingaporeDate();
    const tomorrow = getSingaporeDateRange(1).date;
    
    // Create Date objects for the subscription - fix the date range calculation
    const startDate = new Date(today + 'T00:00:00.000Z');
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6); // Properly calculate 7 days from start date
    
    return {
      startDate,
      endDate,
      todayStr: today,
      tomorrowStr: tomorrow
    };
  };

  const loadSchedules = () => {
    const { startDate, endDate, todayStr } = getDateRange();
    
    const options = {
      startDate,
      endDate,
      orderBy: 'jobDate',
      orderDirection: 'asc',
      // CRITICAL FIX: Use includeAllUnconfirmed instead of includePastDates
      // This allows rescheduled jobs to be retrieved from database
      includeAllUnconfirmed: true,
      includePastDates: true // Allow past jobDate records to be retrieved
    };

    return subscribeToSchedules((schedulesData) => {
      console.log('📅 Staff received schedules (before filtering):', schedulesData.length);
      
      // CRITICAL FIX: Apply client-side filtering for 7-day window
      // Filter based on display date (scheduledDate > jobDate > invoiceDate)
      const sevenDayEndDate = getSingaporeDateRange(6).date; // Today + 6 days = 7 days total
      
      const filteredSchedules = schedulesData.filter(schedule => {
        // Use the same display date logic as admin portal
        const displayDate = getStandardizedDisplayDate(schedule);
        
        if (!displayDate) return false;
        
        // Only show schedules within the 7-day window (today to today+6)
        const isWithinWindow = displayDate >= todayStr && displayDate <= sevenDayEndDate;
        
        // Exclude unconfirmed schedules (staff should only see confirmed jobs)
        const isConfirmed = schedule.status !== 'unconfirmed';
        
        const shouldShow = isWithinWindow && isConfirmed;
        
        if (shouldShow) {
          console.log(`✅ Including schedule: ${schedule.invoiceNumber} - ${displayDate} (${schedule.jobType})`);
        } else {
          console.log(`❌ Excluding schedule: ${schedule.invoiceNumber} - ${displayDate} (window: ${isWithinWindow}, confirmed: ${isConfirmed})`);
        }
        
        return shouldShow;
      });
      
      console.log('📅 Staff schedules after 7-day filtering:', filteredSchedules.length);
      setSchedules(filteredSchedules);
      setLoading(false);
      setRefreshing(false);
    }, options);
  };

  useEffect(() => {
    const unsubscribe = loadSchedules();
    
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    // The subscription will automatically update with new data
    setTimeout(() => setRefreshing(false), 1000);
  };

  // Group schedules by date
  const groupSchedulesByDate = () => {
    const { todayStr, tomorrowStr } = getDateRange();
    const grouped = {};
    
    schedules.forEach(schedule => {
      // Use standardized display date function
      const displayDate = getStandardizedDisplayDate(schedule);
      if (displayDate && !grouped[displayDate]) {
        grouped[displayDate] = [];
      }
      if (displayDate) {
        grouped[displayDate].push(schedule);
      }
    });
    
    // Create ordered array of dates for next 7 days using Singapore timezone
    const dateArray = [];
    for (let i = 0; i < 7; i++) {
      const dateStr = getSingaporeDateRange(i).date;
      
      let label;
      if (isToday(dateStr)) {
        label = 'Today';
      } else if (isTomorrow(dateStr)) {
        label = 'Tomorrow';
      } else {
        label = getDayName(dateStr) + ', ' + formatDateShort(dateStr).split('/').slice(0, 2).join('/');
      }
      
      dateArray.push({
        date: dateStr,
        label,
        schedules: grouped[dateStr] || [],
        isToday: isToday(dateStr),
        isTomorrow: isTomorrow(dateStr)
      });
    }
    
    return dateArray;
  };

  const getJobTypeColor = (jobType) => {
    const colors = {
      'Deliver & Installation': '#52c41a',
      'Dismantle & Collection': '#faad14', 
      'Deliver Only': '#1890ff',
      'Collect Only': '#722ed1',
      'Self Collect': '#13c2c2',
      'Self Return': '#eb2f96',
      'Site Survey': '#f5222d',
      'Site Inspection/Endorsement': '#fa8c16'
    };
    return colors[jobType] || '#666666';
  };

  const formatTime = (schedule) => {
    // Check multiple possible time fields
    const timeStr = schedule.scheduledTime || schedule.time || schedule.jobTime || schedule.preferredTime;
    
    if (!timeStr || timeStr === '') return 'Time TBD';
    
    // Handle different time formats
    if (timeStr.includes(':')) {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    }
    
    return timeStr;
  };

  const JobCard = ({ schedule }) => (
    <Card 
      size="small" 
      style={{ 
        marginBottom: 12,
        borderLeft: `4px solid ${getJobTypeColor(schedule.jobType)}`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <ClockCircleOutlined style={{ marginRight: 8, color: '#666' }} />
            <Text strong style={{ fontSize: '16px' }}>
              {formatTime(schedule)}
            </Text>
            <Tag 
              color={getJobTypeColor(schedule.jobType)}
              style={{ marginLeft: 12 }}
            >
              {schedule.jobType}
            </Tag>
          </div>
          
          <div style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: '14px', color: '#a20000' }}>
              {schedule.contactPerson || schedule.customerName || 'Contact TBD'}
            </Text>
          </div>
          
          {(schedule.customerPhone || schedule.phone) && (
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                📞 {schedule.customerPhone || schedule.phone}
              </Text>
            </div>
          )}
          
          {schedule.siteAddress && (
            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 4 }}>
              <EnvironmentOutlined style={{ marginRight: 6, marginTop: 2, color: '#666', fontSize: '12px' }} />
              <Text type="secondary" style={{ fontSize: '12px', lineHeight: '16px' }}>
                {schedule.siteAddress}
              </Text>
            </div>
          )}
          
          {schedule.configuration && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                📦 {schedule.configuration}
              </Text>
            </div>
          )}
          
          {schedule.invoiceNumber && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                Invoice: {schedule.invoiceNumber}
              </Text>
            </div>
          )}
        </div>
        
        {schedule.status === 'completed' && (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
        )}
      </div>
    </Card>
  );

  const DaySection = ({ dayData, showRefreshButton }) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: 16,
        padding: '8px 0',
        borderBottom: dayData.isToday ? '1px solid #666' : '1px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CalendarOutlined style={{ 
            marginRight: 8, 
            color: '#666',
            fontSize: '16px'
          }} />
          <Title 
            level={4} 
            style={{ 
              margin: 0,
              color: '#333',
              fontWeight: dayData.isToday ? 'bold' : 'normal'
            }}
          >
            {dayData.label}
          </Title>
          {dayData.isToday && (
            <Tag color="blue" style={{ marginLeft: 12 }}>
              {dayData.schedules.length} job{dayData.schedules.length !== 1 ? 's' : ''}
            </Tag>
          )}
        </div>
        {showRefreshButton && (
          <Button
            type="text"
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={handleRefresh}
            size="small"
            style={{ 
              color: 'black'
            }}
          >
            Refresh
          </Button>
        )}
      </div>
      
      {dayData.schedules.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '20px',
          backgroundColor: '#fafafa',
          borderRadius: '6px',
          border: '1px dashed #d9d9d9'
        }}>
          <Text type="secondary">No jobs scheduled</Text>
        </div>
      ) : (
        dayData.schedules.map(schedule => (
          <JobCard key={schedule.id} schedule={schedule} />
        ))
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '300px',
        flexDirection: 'column'
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: 16, color: '#666' }}>
          Loading this week's schedule...
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Text type="danger">Error loading schedules: {error}</Text>
        <br />
        <Button 
          type="primary" 
          onClick={handleRefresh}
          style={{ marginTop: 16 }}
        >
          Try Again
        </Button>
      </div>
    );
  }

  const groupedSchedules = groupSchedulesByDate();
  const totalJobs = schedules.length;

  return (
    <div style={{ padding: '12px 24px 24px 24px' }}>
      {/* Bypass mode indicator */}
      {staffAuthManager.isBypassMode() && (
        <div style={{ 
          marginBottom: 16, 
          padding: '8px 12px', 
          background: '#fff2e8', 
          border: '1px solid #ffb366',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#d46b08'
        }}>
          🔧 BYPASS MODE - Showing sample schedule data for testing
        </div>
      )}

      {/* Schedule content */}
      {totalJobs === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div>
              <Text>No jobs scheduled for this week</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Check back later or contact your supervisor
              </Text>
            </div>
          }
        />
      ) : (
        <div>
          {groupedSchedules.map(dayData => (
            <DaySection key={dayData.date} dayData={dayData} showRefreshButton={dayData.isToday} />
          ))}
          
          <Divider />
          
          <div style={{ 
            textAlign: 'center', 
            padding: '16px',
            backgroundColor: '#f6ffed',
            borderRadius: '6px',
            border: '1px solid #b7eb8f'
          }}>
            <Text style={{ color: '#389e0d', fontSize: '12px' }}>
              📅 Schedule shows jobs for the next 7 days only
              <br />
              Times and locations are subject to change - check with supervisor for updates
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffScheduleView;
